import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  CircularProgress,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { del, get, post, put } from "src/utils/networkUtils";
import MiniGameForm from "./forms/MiniGameForm";
import BannerAdForm from "./forms/BannerAdForm";
import AddIcon from "@mui/icons-material/Add";
import { closestCenter, DndContext } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { COMPONENT_TYPES } from "src/utils/constantsUtils";
import InAppLinkForm from "./forms/InAppLinkForm";

export default function HomeComponentEditPage() {
  const { homeComponentId } = useParams();
  const [searchParams] = useSearchParams();
  const typeParam = Number(searchParams.get("type")) || 0;

  const [componentItems, setComponentItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [componentToDelete, setComponentToDelete] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [gameProviders, setGameProviders] = useState([]);

  const COMPONENT_TYPE_BY_VALUE = Object.values(COMPONENT_TYPES).reduce(
    (acc, item) => {
      acc[item.value] = item.label;
      return acc;
    },
    {}
  );

  const handleGetComponentItems = async () => {
    try {
      setLoading(true);
      const response = await get(
        `/admin/home-component-items?homeComponentId=${homeComponentId}`
      );
      const componentData = response.homeComponentItems;
      if (componentData.length > 0) {
        setComponentItems(componentData);
      } else {
        setComponentItems([]);
      }
    } catch (err) {
      console.error("Error fetching component:", err);
      setError("Failed to load component");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleLoadData = async () => {
      try {
        setLoading(true);

        const requests = [
          get(`/admin/home-component-items?homeComponentId=${homeComponentId}`),
        ];
        if (typeParam === 0) {
          requests.push(
            get(`/admin/game-providers?offset=0&limit=100&isActive=true`)
          );
        }

        const [componentRes, gameProviderRes] = await Promise.all(requests);

        const componentData = componentRes?.homeComponentItems ?? [];
        setComponentItems(componentData);

        if (gameProviderRes) {
          setGameProviders(gameProviderRes.gameProviders ?? []);
        }
      } catch (err) {
        console.error("Error loading data:", err);
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    };
    if (homeComponentId) {
      handleLoadData();
    }
  }, [homeComponentId, typeParam]);

  const handleUpdate = (id, updatedItem) => {
    setComponentItems((prev) =>
      prev.map((item) =>
        String(item.homeComponentItemId) === String(id) ? updatedItem : item
      )
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await post(`/admin/home-component-items/bulk-upsert`, {
        homeComponentItems: componentItems,
      });
      if (response.success) {
        handleGetComponentItems();
      }
    } catch (error) {
      console.error("Error saving components:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateHandler = async (id, updatedItem) => {
    try {
      setSaving(true);
      const response = await put(
        `/admin/home-component-items/${id}`,
        updatedItem
      );
      if (response.success) {
        handleGetComponentItems();
      }
    } catch (error) {
      console.error("Error updating component item:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (id) => {
    setComponentToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!componentToDelete) return;
    setDeleteDialogOpen(false);
    try {
      setDeleting(true);
      const response = await del(
        `/admin/home-component-items/${componentToDelete}`
      );
      if (response.success) {
        handleGetComponentItems();
        setComponentToDelete(null);
      }
    } catch (error) {
      console.error("Error deleting component:", error);
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setComponentToDelete(null);
  };

  const handleCancel = () => {
    handleGetComponentItems();
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (+active?.id !== +over?.id) {
      setComponentItems((items) => {
        const oldIndex = items.findIndex(
          (item) => +item.homeComponentItemId === +active?.id
        );
        const newIndex = items.findIndex(
          (item) => +item.homeComponentItemId === +over?.id
        );
        const reorderedItems = arrayMove(items, oldIndex, newIndex);

        // Update homeComponentOrder for all items based on their new position
        return reorderedItems.map((item, index) => ({
          ...item,
          homeComponentItemOrder: index,
        }));
      });
    }
  };

  const renderForm = () => {
    switch (typeParam) {
      case COMPONENT_TYPES.minigame.value:
        return componentItems.length > 0 ? (
          <>
            <DndContext
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={componentItems
                  .sort(
                    (a, b) =>
                      (a.homeComponentItemOrder ?? 0) -
                      (b.homeComponentItemOrder ?? 0)
                  )
                  .map((c) => c.homeComponentItemId)}
                strategy={verticalListSortingStrategy}
              >
                {componentItems
                  .sort(
                    (a, b) =>
                      (a.homeComponentItemOrder ?? 0) -
                      (b.homeComponentItemOrder ?? 0)
                  )
                  .map((item) => (
                    <MiniGameForm
                      key={item.homeComponentItemId}
                      component={item}
                      onUpdate={handleUpdate}
                      onUpdateHandler={handleUpdateHandler}
                      onCancel={handleCancel}
                      onDelete={handleDeleteClick}
                      disabled={saving || deleting}
                      gameProviders={gameProviders}
                    />
                  ))}
              </SortableContext>
            </DndContext>
          </>
        ) : (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="h6">No {typeLabel} found</Typography>
          </Box>
        );
      case COMPONENT_TYPES.adbanner.value:
        return componentItems.length > 0 ? (
          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={componentItems
                .sort(
                  (a, b) =>
                    (a.homeComponentItemOrder ?? 0) -
                    (b.homeComponentItemOrder ?? 0)
                )
                .map((c) => c.homeComponentItemId)}
              strategy={verticalListSortingStrategy}
            >
              {componentItems
                .sort(
                  (a, b) =>
                    (a.homeComponentItemOrder ?? 0) -
                    (b.homeComponentItemOrder ?? 0)
                )
                .map((item) => (
                  <BannerAdForm
                    key={item.homeComponentItemId}
                    component={item}
                    onUpdate={handleUpdate}
                    onDelete={handleDeleteClick}
                    onUpdateHandler={handleUpdateHandler}
                    disabled={saving || deleting}
                  />
                ))}
            </SortableContext>
          </DndContext>
        ) : (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="h6">No {typeLabel} found</Typography>
          </Box>
        );
      case COMPONENT_TYPES.ultimatefan.value:
        return (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="h6">Ultimate Fan form coming soon</Typography>
          </Box>
        );
      case COMPONENT_TYPES.inapplink.value:
        return componentItems.length > 0 ? (
          <DndContext
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={componentItems
                .sort(
                  (a, b) =>
                    (a.homeComponentItemOrder ?? 0) -
                    (b.homeComponentItemOrder ?? 0)
                )
                .map((c) => c.homeComponentItemId)}
              strategy={verticalListSortingStrategy}
            >
              {componentItems
                .sort(
                  (a, b) =>
                    (a.homeComponentItemOrder ?? 0) -
                    (b.homeComponentItemOrder ?? 0)
                )
                .map((item) => (
                  <InAppLinkForm
                    key={item.homeComponentItemId}
                    component={item}
                    onUpdate={handleUpdate}
                    onDelete={handleDeleteClick}
                    onUpdateHandler={handleUpdateHandler}
                    disabled={saving || deleting}
                  />
                ))}
            </SortableContext>
          </DndContext>
        ) : (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="h6">No {typeLabel} found</Typography>
          </Box>
        );
      default:
        return <Typography>Unknown component type</Typography>;
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography color="error" variant="h6" gutterBottom>
            {error}
          </Typography>
          <Button onClick={handleCancel} variant="contained" sx={{ mt: 2 }}>
            Go Back
          </Button>
        </Paper>
      </Container>
    );
  }

  const typeLabel = COMPONENT_TYPE_BY_VALUE[typeParam];

  const handleAddNew = () => {
    if (typeParam === COMPONENT_TYPES.minigame.value) {
      setComponentItems((prev) => {
        const newComponent = {
          homeComponentItemId: Date.now().toString(),
          miniGameId: null,
          homeComponentId,
          homeComponentItemOrder: prev.length + 1,
          visible: true,
        };
        return [...prev, newComponent];
      });
    } else if (typeParam === COMPONENT_TYPES.adbanner.value) {
      setComponentItems((prev) => {
        const newComponent = {
          homeComponentItemId: Date.now().toString(),
          imageUrl: "",
          title: "",
          clickUrl: "",
          visible: true,
          homeComponentId,
          homeComponentItemOrder: prev.length + 1,
        };
        return [...prev, newComponent];
      });
    }
    else if (typeParam === COMPONENT_TYPES.inapplink.value) {
      setComponentItems((prev) => {
        const newComponent = {
          homeComponentItemId: Date.now().toString(),
          imageUrl: "",
          visible: true,
          homeComponentId,
          inAppPage: null,
          homeComponentItemOrder: prev.length + 1,
        };
        return [...prev, newComponent];
      });
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "#F9FAFB",
        minHeight: "100vh",
        py: 3,
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                fontSize: "31.429px",
                color: "#000",
              }}
            >
              Home Components &gt; {typeLabel}
            </Typography>
          </Box>
          <Box>
            <Stack direction="row" spacing={2}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddNew}
                disabled={saving || deleting}
                sx={{
                  backgroundColor: "#3167ca",
                  color: "white",
                  fontWeight: 700,
                  fontSize: "16px",
                  borderRadius: "8px",
                  boxShadow: "0px 4px 10px 0px rgba(0,0,0,0.15)",
                  px: 3,
                  py: 1.5,
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#2555a8",
                  },
                }}
              >
                {`Add New ${typeLabel}`}
              </Button>
              <Button
                variant="outlined"
                onClick={handleCancel}
                disabled={saving || deleting}
                sx={{
                  borderColor: "#3167ca",
                  color: "#3167ca",
                  fontWeight: 700,
                  fontSize: "17.143px",
                  borderRadius: "8px",
                  boxShadow: "0px 4px 10px 0px rgba(0,0,0,0.15)",
                  minWidth: "107px",
                  height: "47px",
                  textTransform: "none",
                  "&:hover": {
                    borderColor: "#3167ca",
                    backgroundColor: "rgba(49, 103, 202, 0.04)",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="outlined"
                onClick={handleSave}
                disabled={saving || deleting}
                sx={{
                  borderColor: "#3167ca",
                  color: "#3167ca",
                  fontWeight: 700,
                  fontSize: "17.143px",
                  borderRadius: "8px",
                  boxShadow: "0px 4px 10px 0px rgba(0,0,0,0.15)",
                  minWidth: "107px",
                  height: "47px",
                  textTransform: "none",
                  "&:hover": {
                    borderColor: "#3167ca",
                    backgroundColor: "rgba(49, 103, 202, 0.04)",
                  },
                }}
              >
                {saving ? <CircularProgress size={20} /> : "Save"}
              </Button>
            </Stack>
          </Box>
        </Box>
        <Paper
          sx={{
            backgroundColor: "#F5F6F8",
            borderRadius: "16px",
            p: 2,
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              px: 2,
              paddingRight: "0px !important",
            }}
          >
            <Box sx={{ width: "8.33%" }} /> {/* Spacer for drag handle */}
            {typeParam === COMPONENT_TYPES.minigame.value ? (
              <Typography
                sx={{
                  width: "25%",
                  fontWeight: 700,
                  fontSize: "15px",
                  color: "#687581",
                }}
              >
                Mini Game
              </Typography>
            ) : typeParam === COMPONENT_TYPES.adbanner.value ? (
              <>
                <Typography
                  sx={{
                    width: "25%",
                    fontWeight: 700,
                    fontSize: "15px",
                    color: "#687581",
                  }}
                >
                  Image
                </Typography>
                <Typography
                  sx={{
                    width: "20%",
                    fontWeight: 700,
                    fontSize: "15px",
                    color: "#687581",
                  }}
                >
                  Title
                </Typography>
                <Typography
                  sx={{
                    width: "25%",
                    fontWeight: 700,
                    fontSize: "15px",
                    color: "#687581",
                  }}
                >
                  Click URL
                </Typography>
              </>
            ) : typeParam === COMPONENT_TYPES.inapplink.value ? (
              <>
                <Typography
                  sx={{
                    width: "25%",
                    fontWeight: 700,
                    fontSize: "15px",
                    color: "#687581",
                  }}
                >
                  Image
                </Typography>
                <Typography
                  sx={{
                    width: "25%",
                    fontWeight: 700,
                    fontSize: "15px",
                    color: "#687581",
                  }}
                >
                  Visible
                </Typography>
                <Typography
                  sx={{
                    width: "20%",
                    fontWeight: 700,
                    fontSize: "15px",
                    color: "#687581",
                  }}
                >
                  In-App Page
                </Typography>
              </> ) : null}
          </Box>
        </Paper>
        {/* Form */}
        {renderForm()}
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
            sx={{
              backgroundColor: "#3167ca",
              color: "white",
              fontWeight: 700,
              fontSize: "16px",
              borderRadius: "8px",
              boxShadow: "0px 4px 10px 0px rgba(0,0,0,0.15)",
              px: 3,
              py: 1.5,
              textTransform: "none",
              minWidth: "204px",
              "&:hover": {
                backgroundColor: "#2555a8",
              },
            }}
          >
            {`Add New ${typeLabel}`}
          </Button>
        </Box>
        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            Are you sure, want to delete?
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              This action cannot be undone. The component will be permanently
              deleted.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleDeleteCancel}
              sx={{
                color: "#3167ca",
                fontWeight: 600,
              }}
            >
              No
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              autoFocus
              variant="contained"
              sx={{
                backgroundColor: "#FF5274",
                color: "white",
                fontWeight: 600,
                "&:hover": {
                  backgroundColor: "#e04562",
                },
              }}
            >
              Yes
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}
